import React, { useEffect, useRef, useState } from "react";
import AppLayout from "@/Layouts/AppLayout";
import { Dropdown, EmptyState } from "@/Components";
import { X, Search, ChevronDown, Plus } from "react-feather";
import { InertiaLink, usePage } from "@inertiajs/inertia-react";
import { Tabs } from "@/Components/Tabs";
import { Inertia } from "@inertiajs/inertia";
import OrganizationLayout from "@/Layouts/OrganizationLayout";
import Row from "@/Components/Questions/Row";
import { atom, useAtom, useSetAtom } from "jotai";
import { debounce } from "lodash";
import { SCOPE_OPTIONS } from "@/Enums/scopes";
import {
  LabeledInput,
  LabeledMultiSelectAutocomplete,
} from "@/Components/Form";
import { toast } from "react-toastify";
import { LoadingProvider, useLoading } from "@/Contexts/LoadingContext";
import { Folder, Teams as TeamsIcon } from "@/Icons";
import LayoutIcon from "@/Components/LayoutIcon";
import ChooseFromLibrarySidebar from "@/Components/Team/ChooseFromLibrarySidebar";
import NewActivityChooseType from "@/Components/Team/NewActivityChooseType";
import CalculatorRow from "@/Pages/Organization/Calculators/Partials/CalculatorRow";

const searchAtom = atom("");
const sortAtom = atom("created.asc");
const scopesAtom = atom(null);
const tagsAtom = atom(null);

export const isActivitiesLibraryOpenAtom = atom(false);
export const isNewActivityOpenAtom = atom(false);

export default function Index({ errors, tags, calculators }) {
  const user = usePage().props?.auth?.user?.data || {};
  const team = usePage().props?.team?.data || {};
  const createdQuestionId =
    usePage().props?.custom_response_data?.created_question_id || false;

  const { setIsLoaded } = useLoading();

  const [deletingQuestionId, setDeletingQuestionId] = useState(false);
  const [questionEmissions, setQuestionEmissions] = useState(null);
  const [tab, setTab] = useState(0);
  const tabRef = useRef();

  const params = new URLSearchParams(window.location.search);

  useEffect(() => {
    // Check if tab is set in the URL
    const tab = params.get("tab");
    if (tab) {
      setTab(parseInt(tab));
      tabRef.current.setTabIndex(parseInt(tab));
    }
  }, []);

  const questionsSortedByNewlyCreated = () => {
    // return team.questions
    if (!createdQuestionId) {
      return team.questions;
    }

    return team.questions.sort((q) => (createdQuestionId === q.id ? -1 : 1));
  };

  const questionComponents = questionsSortedByNewlyCreated()
    .map((question, i) => showFilteredQuestion(question, question.id))
    .filter((n) => n);

  useEffect(() => {
    if (team.questions.length === 0 || params.has("tab")) {
      return;
    }

    // Set tab 1 as the default tab if we don't have any own activities
    if (questionComponents.length === 0 || createdQuestionId) {
      tabRef.current.setTabIndex(1);
      setTab(1);
    }

    if (!questionEmissions) {
      setIsLoaded(false);

      // Fetch data for all questions
      axios
        .post(route("teams.questions.emissions.data"), {
          questions: team.questions.map((q) => q.id),
          teams: [team.id],
        })
        .then((res) => {
          setQuestionEmissions(res.data);
          setIsLoaded(true);
        });
    }
  }, [team]);

  function showFilteredQuestion(question, id) {
    if (
      (tab === 0 &&
        Number(question.pivot.user_id) === Number(user.id) &&
        question.pivot.active) ||
      tab === 1
    ) {
      return (
        // <ExpandableBlockQuestion key={`question_${id}`} question={question} isNew={createdQuestionId === id}/>
        <Row
          key={`question_${id}`}
          team={team}
          question={question}
          isNew={createdQuestionId === id}
          onDelete={(question, team) =>
            setDeletingQuestionId({ question, team })
          }
          emission={questionEmissions?.[question.id]}
        />
      );
    }

    return null;
  }

  const handleDelete = () => {
    const id = toast.loading("Please wait...");

    Inertia.delete(route("team.question.detach", deletingQuestionId), {
      onSuccess: () => {
        toast.update(id, {
          render: "The question has been removed from your team!",
          type: "success",
          isLoading: false,
          autoClose: 5000,
        });
      },
      onError: () => {
        toast.update(id, {
          render: "Something went wrong when removing the question",
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      },
      onFinish: () => {
        setDeletingQuestionId(null);
      },
    });
  };

  function onTabChange(index) {
    setTab(index);
  }

  const setNewActivityDialogOpen = useSetAtom(isNewActivityOpenAtom);

  return (
    <div className="mb-8">
      <Tabs
        ref={tabRef}
        items={["Your activities ", "All activities", "Calculators"]}
        onTabChange={onTabChange}
      />

      {(tab === 0 || tab === 1) && (
        <>
          <ActivityFilter tagData={tags.data} />
          {questionComponents.length === 0 ? (
            <EmptyState
              title="You have no activities added to your team"
              text="Create a new activity or select one from the library to start reporting"
              buttonText="New activity"
              buttonOnClick={() => setNewActivityDialogOpen(true)}
            />
          ) : (
            <div className="grid gap-2">{questionComponents}</div>
          )}
        </>
      )}

      {tab === 2 && (
        <>
          {calculators?.data &&
            calculators.data.map((calculator) => (
              <CalculatorRow
                key={`calculator_${calculator.id}`}
                calculator={calculator}
                team={team}
              />
            ))}
        </>
      )}

      {!!deletingQuestionId && (
        <div className="fixed left-0 top-0 z-20 flex h-full w-full flex-col justify-center bg-black bg-opacity-50">
          <div
            className="absolute left-0 top-0 h-full w-full"
            onClick={() => setDeletingQuestionId(null)}
          />
          <div className="relative mx-auto w-full max-w-2xl bg-white p-8 align-middle md:p-16">
            <X
              className="absolute right-0 top-0 h-12 w-12 cursor-pointer p-2"
              onClick={() => setDeletingQuestionId(null)}
            />
            <h3 className="mb-3 text-xl font-semibold">
              Are you sure that you want to continue?
            </h3>
            <p>
              Deleting this question will permanently remove it from your team
              and you will not be able to restore it.
            </p>

            <div className="mt-5 flex w-full justify-end border-t pt-8">
              <a
                className="cursor-pointer py-4 text-blue underline"
                onClick={() => setDeletingQuestionId(null)}
              >
                Cancel
              </a>

              <button className="btn ml-3 self-end" onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ActivityFilter = ({ tagData }) => {
  const [search, setSearch] = useAtom(searchAtom);
  const [sort, setSort] = useAtom(sortAtom);
  const [scopes, setScopes] = useAtom(scopesAtom);
  const [tags, setTags] = useAtom(tagsAtom);

  const sortOptions = [
    { value: "title.asc", label: "Name A-Ö" },
    { value: "title.desc", label: "Name Ö-A" },
    { value: "created_at.desc", label: "Newest" },
    { value: "created_at.asc", label: "Oldest" },
    { value: "updated_at.desc", label: "Updated most recent" },
    { value: "updated_at.asc", label: "Updated least recent" },
  ];

  const tagOptions = tagData.map((tag) => {
    return {
      value: String(tag.id),
      label: tag.name,
    };
  });

  const scopeRef = useRef(null);
  const tagRef = useRef(null);

  const handleSearchAndSort = React.useRef(
    debounce((search, sort, scopes, tags) => {
      Inertia.reload({
        only: ["team"],
        data: {
          search: search,
          sort: sort,
          scopes: scopes?.join(","),
          tags: tags?.join(","),
        },
      });
    }, 500),
  ).current;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (!params.get("scopes") || scopes === null) {
      const initialSearch = params.get("search") || "";
      const initialSort = params.get("sort") || "";
      const initialScopes = params.get("scopes")
        ? params.get("scopes").split(",")
        : ["other", ...SCOPE_OPTIONS.map((s) => s.value)];
      const initialTags = params.get("tags")
        ? params.get("tags").split(",")
        : ["other", ...tagOptions.map((s) => s.value)];

      setSearch(initialSearch);
      setSort(initialSort);
      setScopes(initialScopes);
      setTags(initialTags);

      scopeRef.current.setStateOptions((prev) => {
        return prev.map((scope) => {
          return {
            ...scope,
            checked: initialScopes.includes(scope.value),
          };
        });
      });

      tagRef.current.setStateOptions((prev) => {
        return prev.map((tag) => {
          return {
            ...tag,
            checked: initialTags.includes(tag.value),
          };
        });
      });

      refreshData(initialSearch, initialSort, initialScopes, initialTags);
    }
  }, []);

  const refreshData = (search, sort, scopes, tags) => {
    handleSearchAndSort.cancel();
    handleSearchAndSort(search, sort, scopes, tags);
  };

  return (
    <div className="mb-4 flex flex-col items-end justify-between gap-4 sm:flex-row sm:items-center">
      <div className="flex w-full flex-col gap-4 sm:w-auto sm:flex-row sm:items-end">
        <LabeledMultiSelectAutocomplete
          title="Categories and modules"
          className="w-full sm:w-auto"
          ref={tagRef}
          placeholder="All"
          options={[{ value: "other", label: "Other" }, ...tagOptions]}
          defaultValue={tags || []}
          onSelect={(val) => {
            const values = val.map((v) => v.value);
            setTags(values);
            refreshData(search, sort, scopes, values);
          }}
        />

        <LabeledMultiSelectAutocomplete
          className="w-full sm:w-auto"
          ref={scopeRef}
          placeholder="Scopes"
          options={[{ value: "other", label: "Other" }, ...SCOPE_OPTIONS]}
          defaultValue={scopes || []}
          onSelect={(val) => {
            const values = val.map((v) => v.value);
            setScopes(values);
            refreshData(search, sort, values, tags);
          }}
        />

        <LabeledInput
          value={search}
          type="search"
          placeholder="Search"
          className="w-full sm:w-auto"
          unit={search === "" ? <Search className="relative -top-1" /> : null}
          onChange={(e) => {
            const value = e.target.value;
            setSearch(e.target.value);
            refreshData(value, sort, scopes, tags);
          }}
        />
      </div>

      <Dropdown
        options={sortOptions}
        title="Sort on"
        placeholder="Name A-Ö"
        onSelect={(value) => {
          setSort(value);
          refreshData(search, value, scopes, tags);
        }}
      />
    </div>
  );
};

const HeaderActions = ({ team }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const handleClickOutside = (event) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => setOpen(!open);

  const tags = team.questions
    .map((question) => question.tags)
    .flat()
    .filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i)
    .sort((a, b) => a.name.localeCompare(b.name));

  tags.unshift({ id: -1, name: "Uncategorized" });

  const dashboardUrl = route("dashboard", {
    teamFilter: team.id,
    questionFilter: team.questions.map((q) => q.id).join(","),
    locationFilter: team.location_place_id,
    tagFilter: tags.map((t) => t.id).join(","),
  });

  const setLibrarySidebarOpen = useSetAtom(isActivitiesLibraryOpenAtom);
  const setNewActivityDialogOpen = useSetAtom(isNewActivityOpenAtom);

  return (
    <div className="flex flex-wrap items-center justify-end gap-4" ref={ref}>
      <InertiaLink
        href={route("team.edit", team)}
        className="btn-blue-outline py-3"
      >
        Edit team
      </InertiaLink>
      <InertiaLink href={dashboardUrl} className="btn btn-blue grow-0 py-3">
        Show in results
      </InertiaLink>
      <button
        className="btn btn-blue relative grow-0 py-3 pr-2"
        onClick={toggleDropdown}
      >
        <span className="flex justify-between gap-2">
          Create new <ChevronDown />
        </span>
        {open && (
          <div className="shadow-small absolute right-0 top-full z-10 flex w-max flex-col gap-2 bg-white p-4 text-typo">
            <div
              role="button"
              tabIndex="0"
              className="group flex items-center justify-between gap-4 self-stretch px-4 py-2 hover:bg-primary-light"
              onClick={() => setNewActivityDialogOpen(true)}
              onKeyUp={(e) =>
                e.key === "Enter" && setNewActivityDialogOpen(true)
              }
            >
              Activity
              <Plus className="invisible group-hover:visible" />
            </div>

            <InertiaLink
              href={route("calculators.create", team)}
              className="group flex items-center justify-between gap-4 self-stretch px-4 py-2 hover:bg-primary-light"
            >
              Calculator
              <Plus className="invisible group-hover:visible" />
            </InertiaLink>

            <div
              role="button"
              tabIndex="0"
              className="group flex items-center justify-between gap-4 self-stretch px-4 py-2 hover:bg-primary-light"
              onClick={() => setLibrarySidebarOpen(true)}
              onKeyUp={(e) => e.key === "Enter" && setLibrarySidebarOpen(true)}
            >
              Import from library
              <Folder className="invisible group-hover:visible" />
            </div>
          </div>
        )}
      </button>
    </div>
  );
};

Index.layout = (page) => {
  const team = page.props?.team?.data;

  return (
    <AppLayout title={`Reporting ${team?.name}`}>
      <LoadingProvider>
        <OrganizationLayout
          header={team?.name || "No team found"}
          byline={`${team?.questions?.length || "0"} activities in this team`}
          backAction={{
            label: "Back",
            callback: () => Inertia.visit(route("teams")),
          }}
          action={<HeaderActions team={team} />}
          icon={
            <LayoutIcon>
              <TeamsIcon size={40} className="text-black" />
            </LayoutIcon>
          }
        >
          {page}
        </OrganizationLayout>
      </LoadingProvider>
      <LoadingProvider>
        <ChooseFromLibrarySidebar />
      </LoadingProvider>
      <LoadingProvider>
        <NewActivityChooseType source="owner" />
      </LoadingProvider>
    </AppLayout>
  );
};
